import { Link } from "react-router-dom";
import { Instagram, Linkedin, Mail, Youtube } from "lucide-react";
import { LOGO_HEADER_INTRINSIC } from "@/lib/logoHeaderImage";
import { LogoHeaderImage } from "@/components/branding/LogoHeaderImage";
import { IOS_APP_STORE_URL } from "@/lib/appStoreLinks";

const HOME_ANCHORS = [
  { to: "/#home", label: "Home" },
  { to: "/#why-us", label: "Why Us" },
  { to: "/#social", label: "Social" },
] as const;

const SOCIAL_LINKS = [
  { href: "https://www.linkedin.com/company/kryajet", label: "LinkedIn", icon: Linkedin },
  { href: "https://www.instagram.com/kryajet/", label: "Instagram", icon: Instagram },
  { href: "https://www.youtube.com/@kryajet", label: "YouTube", icon: Youtube },
] as const;

const AUDIENCE_LINKS = [
  { to: "/flight-attendants", label: "Flight attendants" },
  { to: "/for-caterers", label: "Caterers" },
] as const;

const LEGAL_LINKS = [
  { to: "/terms-of-service", label: "Terms of Service" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/return-policy", label: "Return Policy" },
  { to: "/cookie-policy", label: "Cookie Policy" },
] as const;

const linkClass =
  "text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm";

export function MarketingFooter() {
  return (
    <footer className="border-t border-border bg-muted/30">
      <div className="container mx-auto px-6 py-14 md:py-16">
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-12 lg:gap-10 xl:gap-12">
          {/* Brand */}
          <div className="space-y-4 lg:col-span-4">
            <Link to="/" className="inline-block">
              <span
                className="block w-[140px] sm:w-[160px]"
                style={{ aspectRatio: `${LOGO_HEADER_INTRINSIC.width} / ${LOGO_HEADER_INTRINSIC.height}` }}
              >
                <LogoHeaderImage className="size-full max-h-8" />
              </span>
            </Link>
            <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
              The operating system for private aviation catering — built for flight attendants and caterers who need clarity and speed on every order.
            </p>
          </div>

          {/* Explore */}
          <div className="space-y-4 lg:col-span-2">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/90">
              Explore
            </h2>
            <ul className="flex flex-col gap-3">
              {HOME_ANCHORS.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className={linkClass}>
                    {label}
                  </Link>
                </li>
              ))}
              {AUDIENCE_LINKS.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className={linkClass}>
                    {label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/blog" className={linkClass}>
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/faq" className={linkClass}>
                  FAQ
                </Link>
              </li>
              <li>
                <a
                  href={IOS_APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClass}
                >
                  Download for iOS
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="space-y-4 lg:col-span-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/90">
              Legal
            </h2>
            <ul className="flex flex-col gap-3">
              {LEGAL_LINKS.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className={linkClass}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="space-y-4 lg:col-span-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/90">
              Contact
            </h2>
            <ul className="flex flex-col gap-3">
              <li>
                <a href="mailto:max@kryajet.com" className={`${linkClass} inline-flex items-center gap-2`}>
                  <Mail className="h-4 w-4 shrink-0" />
                  max@kryajet.com
                </a>
              </li>
              {SOCIAL_LINKS.map(({ href, label, icon: Icon }) => (
                <li key={href}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${linkClass} inline-flex items-center gap-2`}
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden />
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-border pt-8 text-center text-sm text-muted-foreground">
          © {new Date().getFullYear()} Kryajet. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
